"use client"

import { createContext, useContext, useState, useEffect } from "react"
import type { ReactNode } from "react"

export interface CartItem {
  id: string | number // Support both string (from API) and number (legacy)
  name: string
  price: number
  quantity: number
  image: string
  description?: string
  productId?: string // Store original product ID from API
  variations?: Record<string, string> // Selected variation options: {"Size": "Large", "Spice Level": "Hot"}
  dealId?: string // For deal items
  dealSelections?: Record<string, any> // Deal component selections
  itemType?: 'product' | 'deal' // Type of cart item
}

export interface AppliedCoupon {
  code: string;
  discountAmount: number;
  isFreeDelivery: boolean;
  coupon: any;
}

interface CartContextType {
  cartItems: CartItem[]
  appliedCoupon: AppliedCoupon | null
  addToCart: (item: Omit<CartItem, "quantity">) => void
  addDealToCart: (dealId: string, deal: any, selections: Record<string, any>, quantity?: number) => void
  removeFromCart: (id: string | number) => void
  updateQuantity: (id: string | number, quantity: number) => void
  clearCart: () => void
  applyCoupon: (coupon: AppliedCoupon | null) => void
  getTotalPrice: () => number
  getItemCount: () => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("cart")
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error("Error parsing cart from localStorage", e)
          return []
        }
      }
    }
    return []
  })

  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("appliedCoupon")
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error("Error parsing coupon from localStorage", e)
          return null
        }
      }
    }
    return null
  })

  // Save cart and coupon to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems))
    if (appliedCoupon) {
      localStorage.setItem("appliedCoupon", JSON.stringify(appliedCoupon))
    } else {
      localStorage.removeItem("appliedCoupon")
    }
  }, [cartItems, appliedCoupon])

  const addToCart = (item: Omit<CartItem, "quantity">) => {
    setCartItems((prevItems) => {
      const itemType = item.itemType || 'product'

      if (itemType === 'deal') {
        // For deals, find existing item with same dealId and selections
        const existingItem = prevItems.find((i) => {
          if (i.dealId !== item.dealId || i.itemType !== 'deal') return false

          // Compare deal selections
          const itemSelections = item.dealSelections || {}
          const existingSelections = i.dealSelections || {}

          const itemKeys = Object.keys(itemSelections).sort()
          const existingKeys = Object.keys(existingSelections).sort()

          if (itemKeys.length !== existingKeys.length) return false

          return itemKeys.every((key) => {
            const itemSelection = itemSelections[key]
            const existingSelection = existingSelections[key]

            // Compare selection objects
            return JSON.stringify(itemSelection) === JSON.stringify(existingSelection)
          })
        })

        if (existingItem) {
          return prevItems.map((i) => {
            // Match by dealId and selections
            const itemSelections = item.dealSelections || {}
            const iSelections = i.dealSelections || {}
            const itemKeys = Object.keys(itemSelections).sort()
            const iKeys = Object.keys(iSelections).sort()
            const selectionsMatch =
              itemKeys.length === iKeys.length &&
              itemKeys.every((key) => {
                const itemSelection = itemSelections[key]
                const iSelection = iSelections[key]
                return JSON.stringify(itemSelection) === JSON.stringify(iSelection)
              })

            if (i.dealId === item.dealId && selectionsMatch) {
              return { ...i, quantity: i.quantity + 1 }
            }
            return i
          })
        }
      } else {
        // For products, find existing item with same productId and variations
        const existingItem = prevItems.find((i) => {
          if (i.productId !== item.productId) return false

          // Compare variations
          const itemVariations = item.variations || {}
          const existingVariations = i.variations || {}

          const itemKeys = Object.keys(itemVariations).sort()
          const existingKeys = Object.keys(existingVariations).sort()

          if (itemKeys.length !== existingKeys.length) return false

          return itemKeys.every((key) => itemVariations[key] === existingVariations[key])
        })

        if (existingItem) {
          return prevItems.map((i) => {
            // Match by productId and variations
            const itemVariations = item.variations || {}
            const iVariations = i.variations || {}
            const itemKeys = Object.keys(itemVariations).sort()
            const iKeys = Object.keys(iVariations).sort()
            const variationsMatch =
              itemKeys.length === iKeys.length &&
              itemKeys.every((key) => itemVariations[key] === iVariations[key])

            if (i.productId === item.productId && variationsMatch) {
              return { ...i, quantity: i.quantity + 1 }
            }
            return i
          })
        }
      }

      return [...prevItems, { ...item, quantity: 1, itemType }]
    })
  }

  const addDealToCart = (dealId: string, deal: any, selections: Record<string, any>, quantity: number = 1) => {
    // Calculate the total price for the deal with selections
    let totalPrice = deal.price

    // Add extra costs from selected options
    for (const component of deal.components) {
      const componentSelection = selections[component.id]

      if (componentSelection) {
        if (componentSelection.optionId) {
          // Single choice
          const selectedOption = component.options.find((opt: any) => opt.id === componentSelection.optionId)
          if (selectedOption) {
            totalPrice += selectedOption.price
          }
        } else if (componentSelection.optionIds) {
          // Multi choice or optional
          for (const optionId of componentSelection.optionIds) {
            const selectedOption = component.options.find((opt: any) => opt.id === optionId)
            if (selectedOption) {
              totalPrice += selectedOption.price
            }
          }
        }
      }
    }

    const dealCartItem: Omit<CartItem, "quantity"> = {
      id: `deal-${dealId}-${Date.now()}`, // Unique ID for cart item
      name: deal.name,
      price: totalPrice,
      image: deal.image,
      description: deal.description,
      dealId,
      dealSelections: selections,
      itemType: 'deal'
    }

    // Add the specified quantity
    for (let i = 0; i < quantity; i++) {
      addToCart(dealCartItem)
    }
  }

  const removeFromCart = (id: string | number) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id))
  }

  const updateQuantity = (id: string | number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id)
      return
    }
    setCartItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    )
  }

  const clearCart = () => {
    setCartItems([])
    setAppliedCoupon(null)
  }

  const applyCoupon = (coupon: AppliedCoupon | null) => {
    setAppliedCoupon(coupon)
  }

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0)
  }

  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0)
  }

  return (
    <CartContext.Provider
      value={{
        cartItems,
        appliedCoupon,
        addToCart,
        addDealToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        applyCoupon,
        getTotalPrice,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}

